import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { ActionCableProvider, ActionCableConsumer } from 'react-actioncable-provider';
import { getConversation, getConversations } from '../../../api/conversations';

import ActionCable from 'actioncable';
import Context from '../../../utils/context';
import ConversationOtherUserProfile from './legacy_profile_panel/conversation_other_user_profile';
import Conversation from './legacy_conversation';
import Modal from '../../overlays/modal';
import ConversationList from './legacy_conversation_list';
import LoadingScreen from '../../system/loading_screen';
import ConversationRoomLoadingSkeleton from './loading_screens/conversation_room_loading_skeleton';
import PostConversationModal from '../../questionnaire/post_conversation_survey_modal/index_gated.jsx';
import Pagination from '../../system/pagination';
import { useFeatureCallout } from '../../overlays/feature_callout_overlay/use_feature_callout';

import styles from './legacy_unified_layout.module.css';

export default function ConversationRoom({ match }) {
  let conversation_id = match.params.conversation_id;
  let history = useHistory();
  const context = useContext(Context);
  const user = context.auth.getCurrentUser();
  const viewport = context.viewport;
  const isMobile = viewport.getIsMobile();
  const cable = ActionCable.createConsumer(gon.action_cable_url);
  const [conversations, setConversations] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [surveyEligible, setSurveyEligible] = useState(false);
  const [showSurveyBanner, setShowSurveyBanner] = useState(false);

  // The survey banner cannot be dismissed: it stays visible for the whole
  // session while the mentee is eligible and only disappears once the survey is
  // completed (which turns eligibility off). The recurring survey modal is a
  // separate nudge handled below via useFeatureCallout.
  const handleStartSurvey = () => {
    if (conversation?.post_survey_path) {
      history.push(`${conversation.post_survey_path}?source=banner`);
    }
  };

  // Post-conversation survey modal. Dismissing it snoozes the modal for 24
  // hours (the snooze timestamp is persisted server-side via seen_callouts, so
  // it survives reloads and other devices); after the window elapses the modal
  // re-surfaces on the mentee's next session, and this repeats indefinitely
  // until the survey is completed. Completing the survey flips `surveyEligible`
  // off, which permanently stops the modal.
  const SURVEY_MODAL_RESHOW_MS = 24 * 60 * 60 * 1000;
  const surveyModalBlocked = showWelcomeModal;
  const { shouldShow: shouldShowSurveyModal, dismiss: dismissSurveyModal } = useFeatureCallout({
    calloutKey: 'post_conversation_survey_modal',
    context,
    eligible: surveyEligible && Boolean(conversation?.post_survey_push_due),
    blocked: surveyModalBlocked,
    reshowAfterMs: SURVEY_MODAL_RESHOW_MS,
  });

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [conversationListLoading, setConversationListLoading] = useState(false);
  const [activeMobileTab, setActiveMobileTab] = useState('list'); // 'list', 'chat', 'profile'
  const conversationListRequestRef = useRef(0);

  let numConversationsWithUnreadMessages = 0;
  if (conversations) {
    numConversationsWithUnreadMessages = conversations.filter((x) => x.unread_messages > 0).length;
  }

  useEffect(() => {
    if (user.show_welcome_modal) {
      context.auth.userUpdate({ show_welcome_modal: false });
      setShowWelcomeModal(true);
    }
  }, []);

  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;

    let rafId = null;
    let lastHeight = null;

    const updateViewportHeight = () => {
      rafId = null;
      const newHeight = window.visualViewport.height;
      if (newHeight !== lastHeight) {
        lastHeight = newHeight;
        document.documentElement.style.setProperty('--mobile-viewport-height', `${newHeight}px`);
      }
    };

    const handleViewportChange = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(updateViewportHeight);
      }
    };

    updateViewportHeight();
    window.visualViewport.addEventListener('resize', handleViewportChange);
    window.visualViewport.addEventListener('scroll', handleViewportChange);

    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportChange);
      window.visualViewport.removeEventListener('scroll', handleViewportChange);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      document.documentElement.style.removeProperty('--mobile-viewport-height');
    };
  }, [isMobile]);

  // Auto-switch to chat tab on mobile when a conversation is selected
  useEffect(() => {
    if (isMobile && conversation_id) {
      setActiveMobileTab('chat');
    } else if (isMobile && !conversation_id) {
      setActiveMobileTab('list');
    }
  }, [conversation_id, isMobile]);

  const fetchAllConversations = (page = 1, { showListLoader = false } = {}) => {
    const requestId = conversationListRequestRef.current + 1;
    conversationListRequestRef.current = requestId;

    if (showListLoader) {
      setConversationListLoading(true);
    }

    getConversations(
      (conversations, meta) => {
        if (requestId !== conversationListRequestRef.current) {
          return;
        }

        // Filter out conversations without messages for PatientPartner users
        if (user.current_profile_type === 'PatientPartner') {
          conversations = conversations.filter((c) => c.messages.length > 0);
        }

        // Backend now handles sorting by last message activity
        setConversations(conversations);
        setPagination(meta.pagination);
        setCurrentPage(meta.pagination?.current_page || page);
        setConversationListLoading(false);
      },
      page,
      () => {
        if (requestId !== conversationListRequestRef.current) {
          return;
        }

        setConversationListLoading(false);
      }
    );
  };

  const handlePageChange = (page) => {
    if (conversationListLoading || page === pagination?.current_page) {
      return;
    }

    fetchAllConversations(page, { showListLoader: true });
  };

  const fetchConversation = (showLoad) => {
    if (conversation_id) {
      if (showLoad) {
        setLoading(true);
      }
      getConversation(
        conversation_id,
        (conversation) => {
          setConversation(conversation);
          scrollToBottomOfChat();
          setLoading(false);

          const eligibleForSurvey =
            user.current_profile_type === 'Patient' &&
            conversation.conversation_survey &&
            conversation.post_survey_path &&
            conversation.patient_mentor_connection_completed &&
            !conversation.post_survey_completed &&
            conversation.post_survey_completed !== 'not_applicable';

          setSurveyEligible(Boolean(eligibleForSurvey));
          setShowSurveyBanner(Boolean(eligibleForSurvey));
        },

        () => {
          setConversation(null);
          setLoading(false);
          history.push('/member/conversations');
        }
      );
    }
  };

  useEffect(() => {
    fetchAllConversations(currentPage);
    fetchConversation(true);
  }, [conversation_id]);

  const scrollToBottomOfChat = () => {
    var messageBody = document.querySelector('.message-container');
    if (messageBody) {
      messageBody.scrollTop = messageBody.scrollHeight - messageBody.clientHeight;
    }
  };

  const updateRoom = (data) => {
    switch (data.type) {
      case 'message':
        fetchConversation(false);
        if (!conversationListLoading) {
          fetchAllConversations(currentPage);
        }
        break;
      case 'call_update':
        if (!conversation_id || data.conversation_uuid === conversation_id) {
          fetchConversation(false);
        }
        break;
      case 'translation':
        fetchConversation(false);
        break;
      default:
        return;
    }
  };

  // Render left panel (conversation list)
  const renderLeftPanel = () => (
    <div className={styles.scrollable_content}>
      <ConversationList
        conversations={conversations}
        pathToMatch={'/member/conversations/'}
        selectedConversation={match.params.conversation_id}
        isLoading={conversationListLoading}
      />
    </div>
  );

  // Render middle panel (conversation or splash)
  const renderMiddlePanel = () => {
    if (loading) {
      return (
        <div className={styles.loading_panel}>
          <ConversationRoomLoadingSkeleton />
        </div>
      );
    }

    if (!conversation) {
      return (
        <div className={styles.splash_content}>
          <img
            className={styles.splash_icon}
            src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/platform/messages_splash.svg'
            alt='Messages'
          />
          <p className={styles.splash_copy}>
            Select a conversation on the left to view and
            <br /> send messages to another community member.
          </p>
        </div>
      );
    }

    return (
      <Conversation
        conversation={conversation}
        user={user}
        fetchConversation={fetchConversation}
        otherUser={conversation.other_user_conversations[0]}
        numConversationsWithUnreadMessages={numConversationsWithUnreadMessages}
        backPath={isMobile ? null : undefined}
        profilePath={isMobile ? null : undefined}
        showSurveyBanner={showSurveyBanner && surveyEligible && !shouldShowSurveyModal}
        surveyMentorName={conversation.patient_mentor_name?.trim()?.split(/\s+/)?.[0]}
        onStartSurvey={handleStartSurvey}
      />
    );
  };

  // Render right panel (profile)
  const renderRightPanel = () => {
    if (!conversation) {
      return null;
    }

    return (
      <div className={styles.scrollable_content}>
        <ConversationOtherUserProfile otherUser={conversation.other_user_conversations[0]} />
      </div>
    );
  };

  // Mobile tab click handler
  const handleMobileTabClick = (tab) => {
    setActiveMobileTab(tab);

    // Navigate to list view when clicking list tab
    if (tab === 'list' && conversation_id) {
      history.push('/member/conversations');
    }
  };

  return (
    <ActionCableProvider cable={cable}>
      {conversations !== null && (
        <ActionCableConsumer
          channel={{ channel: 'ConversationsChannel', ids: conversations.map((e) => e.uuid) }}
          onReceived={(data) => {
            updateRoom(data);
          }}
        />
      )}
      {conversation_id && !conversations?.some((c) => c.uuid === conversation_id) && (
        <ActionCableConsumer
          channel={{ channel: 'ConversationsChannel', ids: [conversation_id] }}
          onReceived={(data) => {
            updateRoom(data);
          }}
        />
      )}

      <div
        className={`${styles.conversation_room_container} ${isMobile && conversation_id ? styles.conversation_room_fixed : ''}`}
      >
        <div className={styles.conversation_room_wrapper}>
          {/* Desktop: Three panel layout */}
          {!isMobile && (
            <>
              <div className={styles.desktop_panels}>
                {/* Left Panel - Conversation List */}
                <div className={styles.panel_left}>
                  {conversations === null ? (
                    <div className={styles.loading_panel}>
                      <LoadingScreen />
                    </div>
                  ) : (
                    renderLeftPanel()
                  )}
                </div>

                {/* Middle Panel - Conversation */}
                <div className={styles.panel_middle}>{renderMiddlePanel()}</div>

                {/* Right Panel - Profile */}
                <div className={styles.panel_right}>{renderRightPanel()}</div>
              </div>

              {/* Desktop Pagination */}
              {pagination && (
                <div className={styles.pagination_container}>
                  <Pagination
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    loading={conversationListLoading}
                  />
                </div>
              )}
            </>
          )}

          {/* Mobile: Tabbed interface */}
          {isMobile && (
            <div className={styles.mobile_container}>
              {/* Mobile Tabs - Only show when a conversation is selected */}
              {conversation_id && (
                <div className={styles.mobile_tabs}>
                  <button
                    className={`${styles.mobile_tab} ${styles.mobile_tab_back} ${activeMobileTab === 'list' ? styles.mobile_tab_active : ''}`}
                    onClick={() => handleMobileTabClick('list')}
                  >
                    <svg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M7 1L1 7L7 13'
                        stroke='#979797'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    <span className={styles.mobile_tab}>Back</span>
                  </button>
                  <button
                    className={`${styles.mobile_tab} ${activeMobileTab === 'chat' ? styles.mobile_tab_active : ''}`}
                    onClick={() => handleMobileTabClick('chat')}
                    disabled={!conversation}
                  >
                    Chat
                  </button>
                  <button
                    className={`${styles.mobile_tab} ${activeMobileTab === 'profile' ? styles.mobile_tab_active : ''}`}
                    onClick={() => handleMobileTabClick('profile')}
                    disabled={!conversation}
                  >
                    Profile
                  </button>
                </div>
              )}
              {/* Mobile Panel Container */}
              <div className={styles.mobile_panel_container}>
                {/* List Panel */}
                <div
                  className={`${styles.mobile_panel} ${activeMobileTab === 'list' ? styles.mobile_panel_active : ''}`}
                >
                  {conversations === null ? (
                    <div className={styles.loading_panel}>
                      <LoadingScreen />
                    </div>
                  ) : (
                    <div className={styles.scrollable_content}>
                      <ConversationList
                        conversations={conversations}
                        pathToMatch={'/member/conversations/'}
                        isLoading={conversationListLoading}
                      />
                    </div>
                  )}
                  {/* Mobile pagination for list view */}
                  {pagination && activeMobileTab === 'list' && (
                    <div className={styles.pagination_container}>
                      <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        loading={conversationListLoading}
                      />
                    </div>
                  )}
                </div>

                {/* Chat Panel */}
                <div
                  className={`${styles.mobile_panel} ${activeMobileTab === 'chat' ? styles.mobile_panel_active : ''}`}
                >
                  {renderMiddlePanel()}
                </div>

                {/* Profile Panel */}
                <div
                  className={`${styles.mobile_panel} ${activeMobileTab === 'profile' ? styles.mobile_panel_active : ''}`}
                >
                  {renderRightPanel()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showWelcomeModal && (
          <Modal
            className='welcome-message-modal pp-modal'
            content={
              <>
                <h1 style={{ fontSize: '48px' }} className='marg-bottom-10'>
                  Welcome to PatientPartner
                </h1>
                <p className='marg-bottom-15'>
                  <b>PatientPartner mentors are real people and volunteers</b> here to share their personal experience
                  and answer questions you might have about being a patient.
                </p>
                <h5 style={{ color: '#59B6BE', letterSpacing: '1px', fontSize: '16px' }} className='marg-bottom-10'>
                  WHAT'S NEXT
                </h5>
                <ol>
                  <li>
                    <p className='bold' style={{ fontSize: '16px' }}>
                      Send your first message
                    </p>
                    <p style={{ fontSize: '16px' }} className='marg-bottom-10'>
                      Introduce yourself to your mentor and ask your first question!
                    </p>
                  </li>
                  <li>
                    <p className='bold' style={{ fontSize: '16px' }}>
                      Replies from real people
                    </p>
                    <p style={{ fontSize: '16px' }} className='marg-bottom-10'>
                      Mentors typically reply within 24 hours.
                    </p>
                  </li>
                  <li>
                    <p className='bold' style={{ fontSize: '16px' }}>
                      SMS Notification
                    </p>
                    <p style={{ fontSize: '16px' }} className='marg-bottom-10'>
                      We'll send you a text notification letting you know when your mentor replies.
                    </p>
                  </li>
                </ol>
                <button onClick={() => setShowWelcomeModal(false)}>Get Started</button>
              </>
            }
            onClose={() => {
              setShowWelcomeModal(false);
            }}
          />
        )}

        {shouldShowSurveyModal && conversation && (
          <PostConversationModal
            postConversationPath={`${conversation.post_survey_path}?source=in_app_modal`}
            mentorName={conversation.patient_mentor_name?.trim()?.split(/\s+/)?.[0]}
            onDismiss={dismissSurveyModal}
            onClose={dismissSurveyModal}
          />
        )}
      </div>
    </ActionCableProvider>
  );
}
